import type { MapaDeTemas } from './datos.js';

/**
 * El fondo del mapa: un punto por hecho, dibujado en canvas (hasta 3.000 puntos; en SVG serían
 * 3.000 nodos del DOM y en un móvil se nota).
 *
 * Todo lo que es interacción (marcadores, nombres, panel) va en HTML encima, así que el lienzo es
 * solo decorado: `aria-hidden` y sin eventos. Sigue la receta de las piezas de la marca (AGENTS.md
 * § 7) llevada a 2D: nube de puntos teal (el oro, solo en lo que se pulsa: preguntas y pulso), líneas de estructura entre cada tema y
 * sus dos vecinos (la sinapsis), y un pulso dorado que viaja hacia el tema elegido: el oro es lo que
 * actúa. Sin desenfoque, deriva lenta, pausa fuera de la pantalla y, con «reducir movimiento», un
 * único fotograma con la señal ya resuelta.
 *
 * MAPA-03: una sola escena para las dos capas. Abrir un tema es un viaje continuo, no un corte: la
 * cámara se acerca al tema, el resto del mapa se aleja y se funde, los puntos del tema vuelan a su
 * sitio en la segunda capa y la red de sus subtemas se enciende en cascada. Cerrar hace lo mismo al
 * revés. Todo en `requestAnimationFrame`, sin pasar por React en cada fotograma.
 */

export interface EstadoDelLienzo {
  /** Índice del tema elegido y del que está bajo el ratón o el foco; `-1` si no hay. */
  elegido: number;
  encima: number;
  /** Lo mismo dentro de la segunda capa: el subtema elegido y el señalado. */
  sub: number;
  encimaSub: number;
}

interface Colores {
  teal: string;
  tenue: string;
  oro: string;
}

const MARGEN = 24;
const DURACION_DEL_VIAJE = 950;
const PERIODO_DEL_PULSO = 3400;
/** Cuánto se acerca la cámara al tema al abrirlo, al final del viaje. */
const ACERCAMIENTO = 5;

const leerColores = (): Colores => {
  const css = getComputedStyle(document.documentElement);
  const v = (nombre: string, porDefecto: string) => css.getPropertyValue(nombre).trim() || porDefecto;
  return { teal: v('--teal', '#4fb3bc'), tenue: v('--ink-faint', '#7a8d91'), oro: v('--gold', '#c9a94b') };
};

/** easeInOutCubic: arranca y llega con suavidad; la mitad del viaje es la más rápida. */
const suavizar = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2);
const entre = (a: number, b: number, t: number) => a + (b - a) * t;
const limitar = (v: number) => Math.min(1, Math.max(0, v));

/** Opacidad objetivo de cada tema según lo que está elegido o señalado. */
const objetivo = (tema: number, elegido: number, encima: number): number => {
  if (tema === elegido) return 0.95;
  if (tema === encima) return 0.75;
  return elegido === -1 && encima === -1 ? 0.6 : 0.38;
};

/** Lo que se calcula una vez por capa: puntos por tema, la red y las fases de la deriva. */
interface Capa {
  readonly mapa: MapaDeTemas;
  readonly porTema: number[][];
  readonly deOtrosTemas: number[];
  readonly enlaces: [number, number][];
  readonly fases: number[];
  readonly alfas: number[];
}

/**
 * `anterior`: la misma capa antes de recolocarse. Sus opacidades por tema se conservan: al cambiar
 * de tamaño (cada fotograma mientras el bloque crece) empezar de 0,6 hacía parpadear el tema elegido.
 */
function prepararCapa(mapa: MapaDeTemas, anterior?: Capa): Capa {
  const n = mapa.temas.length;
  const porTema: number[][] = Array.from({ length: n + 1 }, () => []);
  // Los de temas que no salen (`-2`) van aparte: son nube, pero sin tema elegible.
  const deOtrosTemas: number[] = [];
  mapa.puntos.forEach((p, i) => (p[2] === -2 ? deOtrosTemas.push(i) : porTema[p[2] + 1]?.push(i)));
  // La red: cada tema con sus dos vecinos más cercanos, sin repetir pares.
  const enlaces: [number, number][] = [];
  mapa.temas.forEach((a, i) => {
    mapa.temas
      .map((b, j) => ({ j, d: Math.hypot(a.x - b.x, a.y - b.y) }))
      .filter(({ j }) => j !== i)
      .sort((u, v) => u.d - v.d)
      .slice(0, 2)
      .forEach(({ j }) => {
        if (!enlaces.some(([u, v]) => (u === i && v === j) || (u === j && v === i))) enlaces.push([i, j]);
      });
  });
  return {
    mapa,
    porTema,
    deOtrosTemas,
    enlaces,
    // Fase fija por punto: así la deriva es continua entre fotogramas.
    fases: mapa.puntos.map((_, i) => (i * 2.399963) % (Math.PI * 2)),
    alfas: anterior !== undefined && anterior.alfas.length === n ? anterior.alfas : Array.from({ length: n }, () => 0.6),
  };
}

/** Cómo se pinta una capa en un fotograma concreto. */
interface Pincel {
  /** Opacidad de toda la capa (1 fija; en el viaje, sube o baja). */
  alfa: number;
  /** Transformación de cámara: acercar `factor` veces alrededor de (`cx`, `cy`), en píxeles. */
  factor: number;
  cx: number;
  cy: number;
  /** Tema cuyos puntos no se pintan (los pinta el viaje). */
  sinPuntosDe: number;
  /** Cuántos enlaces de la red se ven, de 0 a 1, en orden: la cascada. */
  red: number;
  elegido: number;
  encima: number;
  /** Si hay deriva, pulso y líneas del tema elegido (solo en reposo). */
  vivo: boolean;
  /** Desplazamiento del fondo por el ratón (paralaje), en píxeles. */
  fondoX: number;
  fondoY: number;
}

export interface Escena {
  /** Cambia los datos (al medir el tamaño se recolocan los marcadores y cambian las posiciones). */
  ponerPortada: (mapa: MapaDeTemas) => void;
  ponerCapa: (mapa: MapaDeTemas | null) => void;
  /** Viaja a la segunda capa del tema `tema` de la portada; `alTerminar` al llegar. */
  abrir: (tema: number, alTerminar: () => void) => void;
  /** Vuelve a la portada. */
  cerrar: (alTerminar: () => void) => void;
  redibujar: () => void;
  parar: () => void;
}

/**
 * Arranca el dibujo. `estado` se lee en cada fotograma, así que quien lo llama lo cambia en su sitio
 * y avisa con `redibujar` (necesario cuando no hay animación).
 */
export function arrancarLienzo(lienzo: HTMLCanvasElement, portadaInicial: MapaDeTemas, estado: EstadoDelLienzo): Escena {
  const ctx = lienzo.getContext('2d');
  const inerte: Escena = {
    ponerPortada: () => undefined,
    ponerCapa: () => undefined,
    abrir: (_t, fin) => {
      fin();
    },
    cerrar: fin => {
      fin();
    },
    redibujar: () => undefined,
    parar: () => undefined,
  };
  if (ctx === null) return inerte;

  const quieto = window.matchMedia('(prefers-reduced-motion: reduce)');
  let colores = leerColores();
  let ancho = 0;
  let alto = 0;
  let visible = true;
  let pedido = 0;

  let portada = prepararCapa(portadaInicial);
  let capa: Capa | null = null;
  /** El tema de la portada que se abre o está abierto. */
  let abierto = -1;
  /** Progreso del viaje: 0 en la portada, 1 dentro del tema. */
  let progreso = 0;
  let viaje: { desde: number; hasta: number; inicio: number; alTerminar: () => void } | null = null;
  // Paralaje: el ratón mueve un poco el fondo, con retardo.
  let ratonX = 0;
  let ratonY = 0;
  let fondoX = 0;
  let fondoY = 0;

  const ajustarTamano = () => {
    const r = lienzo.getBoundingClientRect();
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    ancho = r.width;
    alto = r.height;
    lienzo.width = Math.round(ancho * dpr);
    lienzo.height = Math.round(alto * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  };

  const px = (x: number) => MARGEN + x * (ancho - 2 * MARGEN);
  const py = (y: number) => MARGEN + y * (alto - 2 * MARGEN);

  const dibujarCapa = (c: Capa, p: Pincel, t: number) => {
    const { mapa } = c;
    const deriva = p.vivo && !quieto.matches ? 1.4 : 0;
    const X = (x: number) => p.cx + (px(x) - p.cx) * p.factor;
    const Y = (y: number) => p.cy + (py(y) - p.cy) * p.factor;
    const dx = (i: number) => Math.sin(t * 0.00045 + (c.fases[i] ?? 0)) * deriva;
    const dy = (i: number) => Math.cos(t * 0.00038 + (c.fases[i] ?? 0) * 1.3) * deriva;
    if (p.alfa <= 0.001) return;

    // Hechos sin tema: el grano del fondo, con paralaje.
    ctx.fillStyle = colores.tenue;
    ctx.globalAlpha = 0.22 * p.alfa;
    ctx.beginPath();
    for (const i of c.porTema[0] ?? []) {
      const q = mapa.puntos[i];
      if (q === undefined) continue;
      ctx.rect(X(q[0]) + p.fondoX - 0.6, Y(q[1]) + p.fondoY - 0.6, 1.2, 1.2);
    }
    ctx.fill();

    // Los temas que no salen: parte de la nube, en teal tenue, con la mitad de paralaje.
    ctx.fillStyle = colores.teal;
    ctx.globalAlpha = 0.28 * p.alfa;
    ctx.beginPath();
    for (const i of c.deOtrosTemas) {
      const q = mapa.puntos[i];
      if (q === undefined) continue;
      const x = X(q[0]) + p.fondoX / 2;
      const y = Y(q[1]) + p.fondoY / 2;
      ctx.moveTo(x + 1.3, y);
      ctx.arc(x, y, 1.3, 0, Math.PI * 2);
    }
    ctx.fill();

    // La red, por debajo de los puntos. En cascada: el enlace k aparece cuando le toca.
    ctx.strokeStyle = colores.teal;
    ctx.lineWidth = 1;
    c.enlaces.forEach(([i, j], k) => {
      const a = mapa.temas[i];
      const b = mapa.temas[j];
      if (a === undefined || b === undefined) return;
      const turno = limitar((p.red * (c.enlaces.length + 3) - k) / 3);
      if (turno <= 0) return;
      const marcado = i === p.elegido || j === p.elegido;
      ctx.globalAlpha = (marcado ? 0.5 : 0.16) * p.alfa * turno;
      ctx.beginPath();
      ctx.moveTo(X(a.x), Y(a.y));
      ctx.lineTo(entre(X(a.x), X(b.x), turno), entre(Y(a.y), Y(b.y), turno));
      ctx.stroke();
    });

    for (let tema = 0; tema < mapa.temas.length; tema++) {
      if (tema === p.sinPuntosDe) continue;
      const destino = objetivo(tema, p.elegido, p.encima);
      const actual = c.alfas[tema] ?? destino;
      c.alfas[tema] = quieto.matches ? destino : actual + (destino - actual) * 0.12;
      const radio = tema === p.elegido ? 2.2 : 1.7;
      ctx.globalAlpha = (c.alfas[tema] ?? destino) * p.alfa;
      // Todo teal: el oro queda para lo que se puede pulsar (las preguntas del mapa, en HTML encima).
      ctx.fillStyle = colores.teal;
      ctx.beginPath();
      for (const i of c.porTema[tema + 1] ?? []) {
        const q = mapa.puntos[i];
        if (q === undefined) continue;
        const x = X(q[0]) + dx(i);
        const y = Y(q[1]) + dy(i);
        ctx.moveTo(x + radio, y);
        ctx.arc(x, y, radio, 0, Math.PI * 2);
      }
      ctx.fill();
    }

    const elegido = mapa.temas[p.elegido];
    if (elegido === undefined || !p.vivo) return;
    ctx.strokeStyle = colores.teal;
    ctx.globalAlpha = 0.22 * p.alfa;
    ctx.beginPath();
    for (const i of (c.porTema[p.elegido + 1] ?? []).filter((_, k) => k % 7 === 0).slice(0, 36)) {
      const q = mapa.puntos[i];
      if (q === undefined) continue;
      ctx.moveTo(X(elegido.x), Y(elegido.y));
      ctx.lineTo(X(q[0]) + dx(i), Y(q[1]) + dy(i));
    }
    ctx.stroke();

    // El pulso: un punto de oro viaja por cada enlace hacia el tema elegido y, al llegar, se
    // abre un anillo. Con «reducir movimiento» no se pinta: el fotograma ya lleva los enlaces marcados.
    if (quieto.matches) return;
    const fase = (t % PERIODO_DEL_PULSO) / PERIODO_DEL_PULSO;
    const avance = Math.min(1, fase / 0.7);
    const suave = avance * avance * (3 - 2 * avance);
    ctx.fillStyle = colores.oro;
    for (const [i, j] of c.enlaces) {
      if (i !== p.elegido && j !== p.elegido) continue;
      const origen = mapa.temas[i === p.elegido ? j : i];
      if (origen === undefined || avance >= 1) continue;
      ctx.globalAlpha = 0.95 * p.alfa;
      ctx.beginPath();
      ctx.arc(entre(X(origen.x), X(elegido.x), suave), entre(Y(origen.y), Y(elegido.y), suave), 2.6, 0, Math.PI * 2);
      ctx.fill();
    }
    if (fase >= 0.7) {
      const abre = (fase - 0.7) / 0.3;
      ctx.strokeStyle = colores.oro;
      ctx.globalAlpha = 0.7 * (1 - abre) * p.alfa;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.arc(X(elegido.x), Y(elegido.y), 10 + abre * 22, 0, Math.PI * 2);
      ctx.stroke();
    }
  };

  /**
   * Los puntos que viajan: los del tema en la portada salen de su sitio (con la cámara) y llegan a
   * los de la segunda capa. No son los mismos hechos uno a uno (la capa trae su propia muestra), así
   * que se emparejan por orden y los que sobran de un lado repiten del otro.
   */
  const dibujarViaje = (e: number) => {
    if (capa === null) return;
    const tema = portada.mapa.temas[abierto];
    if (tema === undefined) return;
    const origen = portada.porTema[abierto + 1] ?? [];
    const destino = capa.mapa.puntos;
    const n = Math.max(origen.length, destino.length);
    if (n === 0 || origen.length === 0 || destino.length === 0) return;
    const cx = px(tema.x);
    const cy = py(tema.y);
    const factor = entre(1, ACERCAMIENTO, e);
    ctx.globalAlpha = 0.65 + 0.3 * e;
    ctx.fillStyle = colores.teal;
    ctx.beginPath();
    for (let k = 0; k < n; k++) {
      const a = portada.mapa.puntos[origen[k % origen.length] ?? 0];
      const b = destino[k % destino.length];
      if (a === undefined || b === undefined) continue;
      // Cada punto sale con un pequeño retraso propio: el grupo se despliega, no se traslada en bloque.
      const propio = suavizar(limitar((e - (k % 17) / 60) / (1 - 16 / 60)));
      const x = entre(cx + (px(a[0]) - cx) * factor, px(b[0]), propio);
      const y = entre(cy + (py(a[1]) - cy) * factor, py(b[1]), propio);
      ctx.moveTo(x + 1.9, y);
      ctx.arc(x, y, 1.9, 0, Math.PI * 2);
    }
    ctx.fill();
  };

  const pintar = (t: number) => {
    // El viaje avanza con el reloj, no con los fotogramas: si uno se pierde, no se ralentiza.
    if (viaje !== null) {
      const bruto = limitar((t - viaje.inicio) / DURACION_DEL_VIAJE);
      progreso = entre(viaje.desde, viaje.hasta, bruto);
      if (bruto >= 1) {
        const fin = viaje.alTerminar;
        viaje = null;
        fin();
      }
    }
    fondoX += (ratonX * 8 - fondoX) * 0.04;
    fondoY += (ratonY * 6 - fondoY) * 0.04;
    const e = suavizar(progreso);
    ctx.clearRect(0, 0, ancho, alto);
    const tema = portada.mapa.temas[abierto];
    const cx = tema === undefined ? 0 : px(tema.x);
    const cy = tema === undefined ? 0 : py(tema.y);
    const enReposo = viaje === null;

    if (progreso < 1 || capa === null) {
      // La portada: la cámara se acerca al tema y el resto se funde mientras se aleja.
      dibujarCapa(
        portada,
        {
          alfa: (1 - e) ** 1.4,
          factor: entre(1, ACERCAMIENTO, e),
          cx,
          cy,
          sinPuntosDe: progreso > 0 ? abierto : -1,
          red: 1,
          elegido: estado.elegido,
          encima: estado.encima,
          vivo: enReposo && progreso === 0,
          fondoX,
          fondoY,
        },
        t,
      );
    }
    if (progreso > 0 && progreso < 1) dibujarViaje(e);
    if (capa !== null && progreso > 0) {
      // La segunda capa: aparece al final del viaje y su red se enciende en cascada.
      dibujarCapa(
        capa,
        {
          alfa: progreso >= 1 ? 1 : limitar((e - 0.55) / 0.45) ** 2,
          factor: 1,
          cx: 0,
          cy: 0,
          sinPuntosDe: -1,
          red: progreso >= 1 ? 1 : limitar((e - 0.5) / 0.5),
          elegido: estado.sub,
          encima: estado.encimaSub,
          vivo: enReposo && progreso === 1,
          fondoX,
          fondoY,
        },
        t,
      );
    }
    ctx.globalAlpha = 1;
  };

  const bucle = (t: number) => {
    pintar(t);
    pedido = (visible && !quieto.matches && !document.hidden) || viaje !== null ? requestAnimationFrame(bucle) : 0;
  };

  const redibujar = () => {
    if (pedido !== 0) return; // el bucle ya está pintando
    if ((quieto.matches || !visible || document.hidden) && viaje === null) pintar(performance.now());
    else pedido = requestAnimationFrame(bucle);
  };

  const viajar = (hasta: number, alTerminar: () => void) => {
    if (quieto.matches) {
      // Sin animación: se llega de golpe, con la señal ya resuelta.
      progreso = hasta;
      viaje = null;
      pintar(performance.now());
      alTerminar();
      return;
    }
    viaje = { desde: progreso, hasta, inicio: performance.now(), alTerminar };
    if (pedido === 0) pedido = requestAnimationFrame(bucle);
  };

  const alCambiarTema = () => {
    colores = leerColores();
    redibujar();
  };
  const alCambiarVisibilidad = () => {
    if (!document.hidden) redibujar();
  };
  const alMoverRaton = (ev: PointerEvent) => {
    const r = lienzo.getBoundingClientRect();
    if (r.width === 0) return;
    ratonX = limitar((ev.clientX - r.left) / r.width) * 2 - 1;
    ratonY = limitar((ev.clientY - r.top) / r.height) * 2 - 1;
  };

  ajustarTamano();
  const tamano = new ResizeObserver(() => {
    ajustarTamano();
    pintar(performance.now());
  });
  tamano.observe(lienzo);
  const vista = new IntersectionObserver(([entrada]) => {
    visible = entrada?.isIntersecting ?? true;
    if (visible) redibujar();
  });
  vista.observe(lienzo);
  const temaDePagina = new MutationObserver(alCambiarTema);
  temaDePagina.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
  const oscuro = window.matchMedia('(prefers-color-scheme: dark)');
  oscuro.addEventListener('change', alCambiarTema);
  quieto.addEventListener('change', redibujar);
  document.addEventListener('visibilitychange', alCambiarVisibilidad);
  window.addEventListener('pointermove', alMoverRaton, { passive: true });

  redibujar();

  return {
    ponerPortada: mapa => {
      portada = prepararCapa(mapa, portada);
      redibujar();
    },
    ponerCapa: mapa => {
      capa = mapa === null ? null : prepararCapa(mapa, capa ?? undefined);
      redibujar();
    },
    abrir: (tema, alTerminar) => {
      abierto = tema;
      viajar(1, alTerminar);
    },
    cerrar: alTerminar => {
      viajar(0, () => {
        capa = null;
        alTerminar();
      });
    },
    redibujar,
    parar: () => {
      cancelAnimationFrame(pedido);
      pedido = 0;
      tamano.disconnect();
      vista.disconnect();
      temaDePagina.disconnect();
      oscuro.removeEventListener('change', alCambiarTema);
      quieto.removeEventListener('change', redibujar);
      document.removeEventListener('visibilitychange', alCambiarVisibilidad);
      window.removeEventListener('pointermove', alMoverRaton);
    },
  };
}

export const MARGEN_DEL_LIENZO = MARGEN;
